import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Anchor, ShieldCheck, TrendingUp, FileText, ChevronRight, AlertTriangle } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Copilot } from "@/components/Copilot";
import { api, money, INVESTOR_DISCLOSURE } from "./api";

export default function InvestorLanding() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/investor/overview").then(({ data }) => setData(data)).catch(() => setData({ error: true }));
  }, []);

  return (
    <div className="bg-ink" data-testid="investor-landing">
      <Navbar />

      {/* Hero — institutional dark band with metallic accents */}
      <section className="relative overflow-hidden bg-[#0A0E10] pb-20 pt-[160px] text-white">
        <div className="pointer-events-none absolute inset-0 opacity-[0.15]" aria-hidden="true"
          style={{ backgroundImage: "linear-gradient(115deg, transparent 40%, rgba(180,190,200,0.35) 50%, transparent 60%)" }} />
        <div className="container-x relative">
          <p className="eyebrow !text-red-500" data-testid="investor-eyebrow">Investor Relations · Xtreme Exotics Watersports LLC</p>
          <h1 className="mt-5 max-w-3xl font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl" data-testid="investor-hero-title">
            {data?.hero || "Participate in a Vehicle-Specific Jet Car Opportunity"}
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/70 sm:text-lg" data-testid="investor-hero-copy">
            {data?.supporting_copy}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/investors/request-access" className="inline-flex items-center gap-2 rounded-full bg-red-700 px-7 py-3.5 font-display text-sm font-bold uppercase tracking-[0.12em] text-white transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-red-600" data-testid="request-access-cta">
              Request Investor Portal Access <ChevronRight size={15} />
            </Link>
            <a href="#investor-model" className="inline-flex items-center gap-2 rounded-full border border-white/25 px-7 py-3.5 font-display text-sm font-bold uppercase tracking-[0.12em] text-white transition-colors hover:border-red-500 hover:text-red-400" data-testid="how-model-works-cta">
              Review How the Model Works
            </a>
          </div>
          <p className="mt-8 max-w-3xl rounded-lg border border-red-500/30 bg-red-950/40 px-4 py-3 text-[11px] leading-relaxed text-red-200" data-testid="investor-confidentiality-banner">
            {INVESTOR_DISCLOSURE}
          </p>
        </div>
      </section>

      {/* Vehicle economics explainer */}
      <section id="investor-model" className="container-x py-20">
        <div className="grid gap-10 lg:grid-cols-[1fr_1.2fr]">
          <div>
            <p className="eyebrow">Vehicle-Specific Economics</p>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-offwhite">Your participation is tied to a specific vehicle — not company-wide profits</h2>
            <p className="mt-4 text-sm leading-relaxed text-muted">
              Each Jet Car is its own economic unit. Investor participation is calculated from <b>Defined Vehicle Profit</b> —
              the amount remaining for that vehicle after costs, fleet partner payouts, itemized expenses, and reserves.
              Defined Vehicle Profit may be zero or negative in any period.
            </p>
            <div className="mt-6 space-y-3">
              <div className="card-line rounded-xl p-5">
                <p className="font-display text-sm font-bold text-offwhite">Weekly revenue share (per vehicle)</p>
                <p className="mt-2 text-sm text-muted">1–15 eligible rental hours: Fleet Partner 40% · Xtreme Exotics 60%</p>
                <p className="text-sm text-muted">16+ eligible hours: Fleet Partner 60% · Xtreme Exotics 40% (retroactive to all hours that week)</p>
              </div>
              <div className="card-line rounded-xl p-5">
                <p className="font-display text-sm font-bold text-offwhite">What Xtreme Exotics handles</p>
                <p className="mt-2 text-sm text-muted">Vehicle acquisition, partner selection, operating standards, training, marketing support, revenue records, insurance, maintenance coordination, and settlement reporting.</p>
              </div>
            </div>
          </div>

          {/* Vehicle cards */}
          <div>
            <div className="mb-4 flex items-baseline justify-between">
              <h3 className="font-display text-lg font-extrabold text-offwhite" data-testid="vehicle-cards-title">Proposed Vehicle Opportunities</h3>
              {data && !data.vehicles_hidden && <span className="font-mono text-xs text-muted">Total acquisition cost: {money(data.total_acquisition_cost)}</span>}
            </div>
            {!data && <p className="py-10 text-sm text-muted">Loading…</p>}
            {data?.vehicles_hidden && <p className="py-10 text-sm text-muted">Vehicle details are available after portal access is approved.</p>}
            <div className="space-y-3">
              {data?.vehicles?.map((v) => (
                <div key={v.vehicle_id} className="card-line rounded-xl p-5" data-testid={`vehicle-card-${v.name.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="font-display text-base font-extrabold text-offwhite">{v.name}</p>
                    <span className="rounded-full border border-yellow-500/40 bg-yellow-400/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-yellow-700">{v.status}</span>
                  </div>
                  <p className="mt-1 font-mono text-xs text-muted">Acquisition cost: {money(v.acquisition_cost)}</p>
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    {v.tiers.map((t) => (
                      <div key={t.funding_pct} className="rounded-lg border border-black/[0.08] bg-surface/60 px-3 py-2 text-center">
                        <p className="font-display text-sm font-extrabold text-offwhite">{t.funding_pct}% · {money(t.amount)}</p>
                        <p className="mt-0.5 text-[10px] uppercase tracking-wider text-muted">Proposed · {t.profit_pct}% of Defined Vehicle Profit</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process steps */}
      <section className="border-y border-black/[0.06] bg-white py-20">
        <div className="container-x">
          <p className="eyebrow">The Process</p>
          <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-offwhite">From access request to funding — step by step</h2>
          <ol className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="process-steps">
            {(data?.process_steps || []).map((s, i) => (
              <li key={s} className="card-line relative rounded-xl p-6">
                <span className="font-mono text-xs font-bold text-red-700">{String(i + 1).padStart(2, "0")}</span>
                <p className="mt-2 font-display text-sm font-bold text-offwhite">{s}</p>
              </li>
            ))}
          </ol>
          <div className="mt-8 grid gap-4 sm:grid-cols-3">
            {[
              { icon: ShieldCheck, t: "Reviewed access", d: "Every portal account is reviewed and approved by Xtreme Exotics before materials are visible." },
              { icon: FileText, t: "Due diligence first", d: "Documents, milestones, insurance and permits are tracked per vehicle before any funding." },
              { icon: TrendingUp, t: "Vehicle-level reporting", d: "Approved investors receive vehicle-specific statements with itemized calculations." },
            ].map((f) => (
              <div key={f.t} className="rounded-xl border border-black/[0.08] bg-ink p-5">
                <f.icon size={18} className="text-red-700" />
                <p className="mt-3 font-display text-sm font-bold text-offwhite">{f.t}</p>
                <p className="mt-1 text-xs leading-relaxed text-muted">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Risk summary */}
      <section className="container-x py-20">
        <div className="rounded-2xl border border-red-500/25 bg-white p-8 sm:p-10" data-testid="risk-summary">
          <p className="eyebrow flex items-center gap-2 !text-red-700"><AlertTriangle size={14} /> Risk Summary</p>
          <ul className="mt-5 space-y-3">
            {(data?.risk_summary || []).map((r) => (
              <li key={r.slice(0, 30)} className="flex gap-3 text-sm leading-relaxed text-muted">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-red-600" /> {r}
              </li>
            ))}
          </ul>
        </div>
        <div className="mt-10 flex flex-wrap items-center gap-4">
          <Link to="/investors/request-access" className="inline-flex items-center gap-2 rounded-full bg-red-700 px-7 py-3.5 font-display text-sm font-bold uppercase tracking-[0.12em] text-white transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-red-600" data-testid="request-access-cta-bottom">
            Request Investor Portal Access <Anchor size={15} />
          </Link>
          <Link to="/investors/login" className="text-sm font-semibold text-muted transition-colors hover:text-red-700" data-testid="investor-login-link">
            Already approved? Investor sign-in
          </Link>
        </div>
        <p className="mt-6 text-xs text-muted">Questions first? Use the assistant in the bottom-right corner — it answers from the public overview only.</p>
      </section>
      <Copilot audience="public" />
      <Footer />
    </div>
  );
}
